import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InlineAuth } from "@/components/InlineAuth";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CityCombobox } from "@/components/CityCombobox";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Package, FileText, MessageCircle, CheckCircle2, Zap, ShieldCheck } from "lucide-react";

const empty = {
  sender_name: "", sender_phone: "", receiver_name: "", receiver_phone: "",
  origin: "", destination: "", pickup_address: "", dropoff_address: "",
  item_type: "dokumen", weight: "", description: "", note: "",
};

export default function SendPackage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ ...empty, origin: params.get("asal") || "", destination: params.get("tujuan") || "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, sender_name: user.name || "", sender_phone: user.phone || "" }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.sender_name || !form.sender_phone || !form.receiver_name || !form.receiver_phone) {
      toast.error("Nama & WhatsApp pengirim dan penerima wajib diisi"); return;
    }
    if (!form.origin || !form.destination) { toast.error("Kota asal dan tujuan wajib dipilih"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/packages", form);
      setResult(data);
      toast.success(`Kiriman ${data.package.package_number} dibuat`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="send-package-page">
      <Seo
        path="/kirim-paket"
        title="Kirim Paket Kilat Barang & Dokumen Antarkota | Travele"
        description="Kirim dokumen & barang antarkota dengan Paket Kilat Travele. Penjemputan door to door, cepat, dan aman. Tarif dikonfirmasi Admin."
      />
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <div className="mb-2 flex items-center gap-2 font-mono-terminal text-xs uppercase tracking-widest text-primary">
          <Zap className="h-3.5 w-3.5" /> Paket Kilat
        </div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Kirim Barang & Dokumen</h1>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          Kirim paket kilat antarkota dengan penjemputan door to door. Isi data di bawah, pesanan diteruskan ke Admin Travele untuk konfirmasi tarif dan penjemputan.
        </p>

        {!user ? (
          <div className="mt-8 border border-border bg-card p-5" data-testid="pkg-auth-gate">
            <div className="mb-3 flex items-start gap-2 rounded-md border border-primary/25 bg-primary/[0.06] p-3 text-xs text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>Kirim paket hanya untuk member. <b className="text-foreground">Masuk</b> atau <b className="text-foreground">daftar gratis</b> memakai nomor WhatsApp Anda untuk melanjutkan.</span>
            </div>
            <InlineAuth />
          </div>
        ) : !result ? (
          <div className="mt-8 grid gap-4 border border-border bg-card p-5">
            <div className="grid gap-1.5">
              <Label>Jenis Kiriman</Label>
              <div className="grid grid-cols-2 gap-2">
                {[{ v: "dokumen", l: "Dokumen", icon: FileText }, { v: "barang", l: "Barang", icon: Package }].map((o) => (
                  <button
                    key={o.v} type="button" data-testid={`item-type-${o.v}`}
                    onClick={() => setForm({ ...form, item_type: o.v })}
                    className={`flex items-center justify-center gap-2 border px-3 py-3 text-sm font-semibold transition-colors ${form.item_type === o.v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                  >
                    <o.icon className="h-4 w-4" /> {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Kota Asal</Label>
                <CityCombobox cities={cities} value={form.origin} onChange={(v) => setForm({ ...form, origin: v })}
                  placeholder="Cari kota asal" testId="pkg-origin" />
              </div>
              <div className="grid gap-1.5"><Label>Kota Tujuan</Label>
                <CityCombobox cities={cities} value={form.destination} onChange={(v) => setForm({ ...form, destination: v })}
                  placeholder="Cari kota tujuan" testId="pkg-destination" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Nama Pengirim</Label><Input data-testid="pkg-sender-name" value={form.sender_name} disabled readOnly className="disabled:cursor-not-allowed disabled:opacity-70" /></div>
              <div className="grid gap-1.5"><Label>WA Pengirim</Label><Input data-testid="pkg-sender-phone" value={form.sender_phone} disabled readOnly className="disabled:cursor-not-allowed disabled:opacity-70" /></div>
            </div>
            <p className="-mt-2 text-[11px] text-muted-foreground">Nama & WhatsApp pengirim sesuai akun member Anda.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Nama Penerima</Label><Input data-testid="pkg-receiver-name" value={form.receiver_name} onChange={set("receiver_name")} placeholder="Nama lengkap" /></div>
              <div className="grid gap-1.5"><Label>WA Penerima</Label><Input data-testid="pkg-receiver-phone" value={form.receiver_phone} onChange={set("receiver_phone")} placeholder="08xxxx" /></div>
            </div>

            <div className="grid gap-1.5"><Label>Alamat Penjemputan</Label><Input data-testid="pkg-pickup" value={form.pickup_address} onChange={set("pickup_address")} placeholder="Alamat lengkap penjemputan" /></div>
            <div className="grid gap-1.5"><Label>Alamat Tujuan</Label><Input data-testid="pkg-dropoff" value={form.dropoff_address} onChange={set("dropoff_address")} placeholder="Alamat lengkap tujuan" /></div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Perkiraan Berat</Label><Input data-testid="pkg-weight" value={form.weight} onChange={set("weight")} placeholder="cth. 2 kg" /></div>
              <div className="grid gap-1.5"><Label>Deskripsi Isi</Label><Input data-testid="pkg-description" value={form.description} onChange={set("description")} placeholder="cth. dokumen kontrak" /></div>
            </div>
            <div className="grid gap-1.5"><Label>Catatan</Label><Textarea data-testid="pkg-note" value={form.note} onChange={set("note")} placeholder="Catatan tambahan (opsional)" /></div>

            <Button data-testid="pkg-submit" onClick={submit} disabled={loading} className="w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90">
              {loading ? "Memproses..." : "Kirim & Lanjut ke WhatsApp Admin"}
            </Button>
          </div>
        ) : (
          <div className="mt-8 border border-border bg-card p-6 text-center" data-testid="pkg-success">
            <CheckCircle2 className="mx-auto mb-3 h-14 w-14 text-emerald-400" />
            <h3 className="font-display text-2xl font-bold">Kiriman Dibuat!</h3>
            <p className="mt-1 font-mono-terminal text-lg text-primary">{result.package.package_number}</p>
            <p className="mt-2 text-sm text-muted-foreground">Lanjutkan ke WhatsApp Admin Travele untuk konfirmasi tarif dan penjemputan.</p>
            <pre className="mt-4 max-h-40 overflow-y-auto whitespace-pre-wrap border border-border bg-background p-3 text-left text-xs text-muted-foreground">{result.wa_message}</pre>
            <a data-testid="pkg-wa-link" href={result.wa_link} target="_blank" rel="noopener noreferrer">
              <Button className="mt-4 w-full bg-[#25D366] font-bold text-black hover:bg-[#25D366]/90">
                <MessageCircle className="mr-2 h-4 w-4" /> Kirim via WhatsApp Admin
              </Button>
            </a>
            <Link to="/"><Button variant="ghost" className="mt-2 w-full">Kembali ke Beranda</Button></Link>
          </div>
        )}
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
